import type { ReactNode } from 'react'
import { Controller, type FieldPath, type FieldValues, useFormContext } from 'react-hook-form'
import { Autocomplete, TextField } from '@mui/material'
import type { TDictionaryItem } from '../../api/types'
import { IconArrowDown } from '../../icons/IconArrowDown'

type DictionaryMultiSelectControlProps = {
  dictionary: TDictionaryItem[]
  placeholder: string
  value: number[]
  onChange: (ids: number[]) => void
  onBlur?: () => void
  name?: string
  error?: boolean
  helperText?: ReactNode
  disabled?: boolean
}

/** Controlled multi-select for dictionary ids — эталон визуала профиля. */
export const DictionaryMultiSelectControl = ({
  dictionary,
  placeholder,
  value,
  onChange,
  onBlur,
  name,
  error,
  helperText,
  disabled = false,
}: DictionaryMultiSelectControlProps) => {
  const ids = Array.isArray(value) ? value : []
  const selected = dictionary.filter((d) => ids.includes(d.id))

  return (
    <Autocomplete<TDictionaryItem, true, false, false>
      multiple
      disableCloseOnSelect
      disabled={disabled}
      options={dictionary}
      value={selected}
      onChange={(_, newValue) => {
        onChange(newValue.map((o) => o.id))
      }}
      onBlur={onBlur}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      popupIcon={<IconArrowDown />}
      fullWidth
      size="small"
      noOptionsText="Ничего не найдено"
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          name={name}
          error={Boolean(error)}
          helperText={helperText}
        />
      )}
    />
  )
}

type DictionaryMultiSelectProps<TFormValues extends FieldValues> = {
  name: FieldPath<TFormValues>
  dictionary: TDictionaryItem[]
  placeholder: string
}

export const DictionaryMultiSelect = <TFormValues extends FieldValues>({
  name,
  dictionary,
  placeholder,
}: DictionaryMultiSelectProps<TFormValues>) => {
  const { control } = useFormContext<TFormValues>()

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const ids: number[] = Array.isArray(field.value) ? field.value : []
        return (
          <DictionaryMultiSelectControl
            dictionary={dictionary}
            placeholder={placeholder}
            value={ids}
            onChange={field.onChange}
            onBlur={field.onBlur}
            name={field.name}
            error={fieldState.invalid}
            helperText={fieldState.error?.message}
          />
        )
      }}
    />
  )
}
